import {skipToken} from '@reduxjs/toolkit/query';
import {match} from 'ts-pattern';

import {isNotEmpty, isNotNil} from 'ramda';

import {useGetTenantIntegrationsAdvancedSearchQuery} from '@dms/api/core';

import {MAZDA_PORTAL_INTEGRATION_CODE} from '../constants/mazdaPortalIntegrationCode';
import {useTenant} from './useTenant';

interface UseIsRegistrationPlateDecoderEnabledProps {
  isSkipped?: boolean;
}

/**
 * Resolves whether the tenant may decode a vehicle's VIN from its registration plate.
 * The lookup requires an integration that provides it - initially MAZDA_PORTAL - to be linked
 * to at least one authorization profile, hence the array of codes ready for more of them.
 */
export function useIsRegistrationPlateDecoderEnabled(
  props?: UseIsRegistrationPlateDecoderEnabledProps
) {
  const {tenant, isLoading: isTenantLoading} = useTenant();

  const tenantId = tenant?.id;
  const isEntitledToQuery = !props?.isSkipped && isNotNil(tenantId);

  const tenantIntegrationsQuery = useGetTenantIntegrationsAdvancedSearchQuery(
    isEntitledToQuery ? {tenantId, integrationCode: [MAZDA_PORTAL_INTEGRATION_CODE]} : skipToken
  );

  const isLoading = match({isSkipped: Boolean(props?.isSkipped), isEntitledToQuery})
    .with({isSkipped: true}, () => false)
    .with(
      {isEntitledToQuery: true},
      () => tenantIntegrationsQuery.isUninitialized || tenantIntegrationsQuery.isLoading
    )
    .otherwise(() => isTenantLoading);

  return {
    isRegistrationPlateDecoderEnabled:
      tenantIntegrationsQuery.data?.some((integration) =>
        isNotEmpty(integration.authorizationProfileIds ?? [])
      ) ?? false,
    isLoading,
  };
}
